import React, {useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import {ArrowBack, Article, Download, Email, Flight, FlightTakeoff, ManageAccounts, Notes, RestartAlt} from '@mui/icons-material';
import {t} from 'ttag';
import {DateTime} from 'luxon';
import {useLocation} from 'react-router-dom';
import {IDelay, IFlight, IPassenger} from '../../store/apis/interfaces/flight';
import {DelayReason} from './DelayDetailsPage';
import useFadeNavigate from './useFadeNavigate';
import FlightStatusBadge from './FlightStatusBadge';
import {useGenerateDelayDeclarationMutation} from '../../store/apis/ccs';

const fmt = (iso: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat('HH:mm') : '—';

const ataStaMinutes = (flight: IFlight): number => {
  const sta = flight.arrival?.scheduledTime;
  const ata = flight.arrival?.actualTime ?? flight.arrival?.estimatedTime;
  if (!sta || !ata) return 0;
  return Math.max(0, Math.round(DateTime.fromISO(ata).diff(DateTime.fromISO(sta), 'minutes').minutes));
};


interface LocationState {
  flight: IFlight;
  carrier: string;
  flightNumber: string;
  date: string;
  selectedPassengers: IPassenger[];
  declarationType: 'delay';
  selectedDelayReasons: DelayReason[];
  selectedDelayEntries?: IDelay[];
}

interface PassengerIdData {
  documentType: string;
  idNumber: string;
  nif: string;
}

const DOC_TYPES = [
  {value: 'cc', label: 'Cartão de Cidadão'},
  {value: 'bi', label: 'Bilhete de Identidade'},
  {value: 'p',  label: 'Passaporte'},
  {value: 'rr', label: 'Autorização de Residência'},
];

const STEPS = [t`Declaration Type`, t`Delay Reason`, t`Review`];

const RECIPIENT_ENTITIES = [
  {value: 'passenger', label: t`Passenger`},
  {value: 'employer', label: t`Employer`},
  {value: 'insurance', label: t`Insurance Company`},
  {value: 'travel_agency', label: t`Travel Agency`},
  {value: 'other', label: t`Other`}
];

const DelayReviewPage = () => {
  const navigate = useFadeNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [generateDeclaration, {isLoading}] = useGenerateDelayDeclarationMutation();
  const [passengerIds, setPassengerIds] = useState<PassengerIdData[]>(() =>
    ((location.state as LocationState | null)?.selectedPassengers ?? [])
      .map(() => ({documentType: '', idNumber: '', nif: ''}))
  );
  const [passengerIdErrors, setPassengerIdErrors] = useState<boolean[]>([]);
  const [recipientEntity, setRecipientEntity] = useState('passenger');
  const [pendingMethod, setPendingMethod] = useState<'email' | 'download' | null>(null);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({recipientEntity: false, recipientEmail: false});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const commitEmailInput = (): boolean => {
    const raw = emailInput.trim().replace(/,\s*$/, '');
    if (!raw) return true;
    if (!EMAIL_REGEX.test(raw)) { setEmailInputError(t`Invalid email address`); return false; }
    if (emails.includes(raw)) { setEmailInputError(t`Email already added`); return false; }
    setEmails((prev) => [...prev, raw]);
    setEmailInput('');
    setEmailInputError(null);
    return true;
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commitEmailInput(); }
    else if (e.key === 'Backspace' && emailInput === '' && emails.length > 0) {
      setEmails((prev) => prev.slice(0, -1));
    }
  };

  if (!state?.flight) {
    return (
      <Box sx={{textAlign: 'center', mt: 4}}>
        <Typography color='error' gutterBottom>
          {t`No flight data. Please go back and search again.`}
        </Typography>
        <Button
          variant='outlined'
          startIcon={<ArrowBack />}
          onClick={() => navigate('/declarations/flight')}
          sx={{mt: 1}}
        >
          {t`Back to Search`}
        </Button>
      </Box>
    );
  }

  const {flight, carrier, flightNumber, date, selectedPassengers, selectedDelayReasons, selectedDelayEntries: stateDelayEntries} = state;

  const formattedDate = DateTime.fromFormat(date, 'yyyy-MM-dd').toLocaleString(DateTime.DATE_FULL);

  const allFlightDelays = [
    ...(flight.departure.delays ?? []),
    ...(flight.arrival.delays ?? [])
  ].filter((d, i, arr) => arr.findIndex(x => x.code === d.code) === i);

  const selectedDelayEntries = stateDelayEntries ?? allFlightDelays;

  const totalDelayMinutes = ataStaMinutes(flight);

  const hasDelay = totalDelayMinutes > 0;

  const passengerNames = selectedPassengers
    .map((p) => `${p.paxGivenName?.[0] ?? '?'}.${p.paxSurname || p.paxGivenName}`)
    .join('; ');

  const fmtTime = (iso: string | null) =>
    iso ? DateTime.fromISO(iso).toFormat('HH:mm') : '';

  const handlePreview = async (method: 'email' | 'download') => {
    if (method === 'email') commitEmailInput();
    const nextErrors = {
      recipientEntity: !recipientEntity,
      recipientEmail: method === 'email' && emails.length === 0 && !emailInput.trim()
    };
    setErrors(nextErrors);
    const idErrors = passengerIds.map(pid => !pid.documentType || !pid.idNumber.trim());
    setPassengerIdErrors(idErrors);
    if (nextErrors.recipientEntity || nextErrors.recipientEmail || idErrors.some(Boolean)) return;

    setSubmitError(null);
    setSubmitSuccess(null);
    setPendingMethod(method);

    const result = await generateDeclaration({
      carrier,
      flightNumber,
      date,
      origin: flight.origin,
      destination: flight.destination,
      scheduledDeparture: fmtTime(flight.departure.scheduledTime),
      estimatedDeparture: fmtTime(flight.departure.estimatedTime),
      actualDeparture: fmtTime(flight.departure.actualTime),
      scheduledArrival: fmtTime(flight.arrival.scheduledTime),
      estimatedArrival: fmtTime(flight.arrival.estimatedTime),
      actualArrival: fmtTime(flight.arrival.actualTime),
      totalDelayMinutes,
      passengers: selectedPassengers.map((p, i) => ({
        name: [p.paxSurname, p.paxGivenName].filter(Boolean).join('/'),
        pnr: p.pnr ?? '',
        ticketNumber: p.ticketNumber ?? '',
        documentType: passengerIds[i]?.documentType ?? '',
        idNumber: passengerIds[i]?.idNumber ?? '',
        nif: passengerIds[i]?.nif ?? ''
      })),
      reasons: selectedDelayReasons.map(r => ({
        code: r.code,
        labelEN: r.labelEN,
        labelPT: r.labelPT,
        textPT: r.textPT,
        textEN: r.textEN
      })),
      recipientEntity,
      deliveryMethod: method,
      emails: method === 'email' ? emails : [],
      notes
    });

    setPendingMethod(null);

    if ('error' in result) {
      setSubmitError((result.error as any)?.error ?? t`An error occurred. Please try again.`);
      return;
    }

    if (method === 'download') {
      const blob = result.data as Blob;
      const isZip = blob.type === 'application/zip';
      const filename = isZip
        ? `delay-declarations-${carrier}${flightNumber}-${date}.zip`
        : `delay-declaration-${carrier}${flightNumber}-${date}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      setSubmitSuccess(t`Declaration sent successfully.`);
    }
  };

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      {/* Flight header */}
      <Card
        elevation={2}
        sx={{borderTop: '3px solid', borderColor: hasDelay ? 'warning.main' : 'primary.main'}}
      >
        <CardContent sx={{py: 2, '&:last-child': {pb: 2}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
            <Box sx={{
              background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
              color: 'white',
              borderRadius: 2,
              width: 48,
              height: 48,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 3px 10px rgba(1,87,155,0.45)',
              gap: 0.25,
            }}>
              <Typography sx={{fontWeight: 900, fontSize: '0.9rem', letterSpacing: '0.1em', lineHeight: 1}}>
                {carrier.toUpperCase()}
              </Typography>
              <FlightTakeoff sx={{fontSize: '0.65rem', opacity: 0.75}} />
            </Box>

            <Box sx={{flex: 1, minWidth: 0}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                <Typography variant='h6' fontWeight='bold' lineHeight={1.2}>
                  {carrier.toUpperCase()}{flightNumber}
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <Typography variant='body1' fontWeight='bold'>{flight.origin}</Typography>
                  <Flight sx={{fontSize: '1rem', color: 'text.secondary'}} />
                  <Typography variant='body1' fontWeight='bold'>{flight.destination}</Typography>
                </Box>
                <FlightStatusBadge status={flight.status} hasDelay={hasDelay} />
              </Box>
              <Typography variant='body2' color='text.secondary'>
                {formattedDate}
                {flight.departure.scheduledTime && ` · STD: ${fmt(flight.departure.scheduledTime)}`}
                {flight.departure.actualTime && ` · ATD: ${fmt(flight.departure.actualTime)}`}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Passengers summary */}
      <Card variant='outlined' sx={{bgcolor: 'grey.50'}}>
        <CardContent sx={{py: 1.5, '&:last-child': {pb: 1.5}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Article sx={{fontSize: '1.1rem', color: 'text.secondary'}} />
            <Typography variant='body2'>
              {t`Issue declaration for`}{' '}
              <Box component='span' fontWeight='bold'>{selectedPassengers.length}</Box>{' '}
              {t`passengers`}{': '}
              <Box component='span' fontWeight='bold'>{passengerNames}</Box>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Stepper */}
      <Stepper activeStep={2} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Passenger Identification */}
      <Card elevation={3}>
        <CardContent sx={{p: 3}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
            <ManageAccounts sx={{color: 'text.secondary'}} />
            <Typography variant='h6' fontWeight='bold'>{t`Passenger Identification`}</Typography>
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{mb: 2.5}}>
            {t`Confirm or complete the identification details. These fields will be included individually in each passenger's declaration.`}
          </Typography>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
            {selectedPassengers.map((p, i) => {
              const hasError = passengerIdErrors[i];
              const displayName = [p.paxSurname, p.paxGivenName].filter(Boolean).join(', ');
              return (
                <Box key={i} sx={{border: '1px solid', borderColor: hasError ? 'error.light' : 'divider', borderRadius: 1, p: 2}}>
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5}}>
                    <Typography fontWeight='bold'>{i + 1}. {displayName}</Typography>
                    {p.pnr && (
                      <Typography variant='caption' color='text.secondary' sx={{fontWeight: 600}}>
                        {'PNR '}
                        <Box component='span' sx={{color: 'primary.main', fontFamily: 'monospace'}}>{p.pnr}</Box>
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5}}>
                    <FormControl size='small' error={hasError && !passengerIds[i]?.documentType}>
                      <InputLabel>{t`Document Type`} *</InputLabel>
                      <Select
                        value={passengerIds[i]?.documentType ?? ''}
                        label={`${t`Document Type`} *`}
                        onChange={(e) => {
                          setPassengerIds(prev => { const next = [...prev]; next[i] = {...next[i], documentType: e.target.value}; return next; });
                          setPassengerIdErrors(prev => { const next = [...prev]; next[i] = false; return next; });
                        }}
                      >
                        {DOC_TYPES.map(dt => <MenuItem key={dt.value} value={dt.value}>{dt.label}</MenuItem>)}
                      </Select>
                      {hasError && !passengerIds[i]?.documentType && <FormHelperText>{t`Required`}</FormHelperText>}
                    </FormControl>
                    <TextField
                      size='small'
                      label={`${t`ID Number`} *`}
                      value={passengerIds[i]?.idNumber ?? ''}
                      error={hasError && !passengerIds[i]?.idNumber?.trim()}
                      helperText={hasError && !passengerIds[i]?.idNumber?.trim() ? t`Required` : undefined}
                      onChange={(e) => {
                        setPassengerIds(prev => { const next = [...prev]; next[i] = {...next[i], idNumber: e.target.value}; return next; });
                        setPassengerIdErrors(prev => { const next = [...prev]; next[i] = false; return next; });
                      }}
                    />
                    <TextField
                      size='small'
                      label={t`Tax ID (optional)`}
                      placeholder={t`9 digits`}
                      value={passengerIds[i]?.nif ?? ''}
                      onChange={(e) => setPassengerIds(prev => { const next = [...prev]; next[i] = {...next[i], nif: e.target.value}; return next; })}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Recipient details form */}
      <Card elevation={3}>
        <CardContent sx={{p: 3}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
            <Article sx={{color: 'text.secondary'}} />
            <Typography variant='h6' fontWeight='bold'>{t`Recipient Details`}</Typography>
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{mb: 3}}>
            {t`Indicate who the declaration will be issued to and the destination email.`}
          </Typography>

          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.5}}>
            {/* Recipient Entity */}
            <FormControl fullWidth error={errors.recipientEntity}>
              <InputLabel>{t`Recipient Entity`} *</InputLabel>
              <Select
                value={recipientEntity}
                label={`${t`Recipient Entity`} *`}
                onChange={(e) => {
                  setRecipientEntity(e.target.value);
                  setErrors((prev) => ({...prev, recipientEntity: false}));
                }}
              >
                {RECIPIENT_ENTITIES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.recipientEntity && (
                <FormHelperText>{t`Please select a recipient entity`}</FormHelperText>
              )}
            </FormControl>

            {/* Recipient Email(s) */}
            <Box>
              <Typography variant='body2' fontWeight='medium' sx={{mb: 0.75}}>{t`Recipient Email(s)`} *</Typography>
              <Box
                sx={{
                  display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center',
                  px: 1.25, py: 0.75, minHeight: 42,
                  border: '1px solid',
                  borderColor: (errors.recipientEmail || emailInputError) ? 'error.main' : 'rgba(0,0,0,0.23)',
                  borderRadius: 1,
                  '&:focus-within': {borderColor: 'primary.main', borderWidth: '2px'},
                  cursor: 'text',
                }}
                onClick={() => (document.getElementById('delay-email-input') as HTMLInputElement | null)?.focus()}
              >
                {emails.map((email, i) => (
                  <Chip key={i} label={email} size='small' onDelete={() => setEmails((p) => p.filter((_, j) => j !== i))} sx={{height: 26}} />
                ))}
                <Box
                  component='input'
                  id='delay-email-input'
                  value={emailInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setEmailInput(e.target.value); setEmailInputError(null); }}
                  onKeyDown={handleEmailKeyDown}
                  onBlur={() => commitEmailInput()}
                  placeholder={emails.length === 0 ? 'email@example.com' : ''}
                  sx={{border: 'none', outline: 'none', flex: 1, minWidth: 180, fontSize: '0.875rem', fontFamily: 'inherit', bgcolor: 'transparent', color: 'text.primary', py: 0.25}}
                />
              </Box>
              {emailInputError && <Typography variant='caption' color='error' sx={{mt: 0.5, display: 'block'}}>{emailInputError}</Typography>}
              {errors.recipientEmail && !emailInputError && <Typography variant='caption' color='error' sx={{mt: 0.5, display: 'block'}}>{t`At least one email address is required.`}</Typography>}
              <Typography variant='caption' color='text.secondary' sx={{mt: 0.5, display: 'block'}}>{t`Press Enter or comma to add each email.`}</Typography>
            </Box>

            {/* Additional Notes */}
            <Box>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75}}>
                <Notes sx={{fontSize: '1rem', color: 'text.disabled'}} />
                <Typography variant='body2' color='text.secondary'>{t`Additional Notes`} ({t`optional`})</Typography>
              </Box>
              <TextField
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t`Additional information to include in the declaration...`}
                multiline
                rows={2}
                fullWidth
                inputProps={{maxLength: 500}}
                FormHelperTextProps={{sx: {textAlign: 'right'}}}
                helperText={`${notes.length}/500`}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Selected delay reasons summary */}
      {selectedDelayReasons.length > 0 && (
        <Card variant='outlined' sx={{bgcolor: 'grey.50'}}>
          <CardContent sx={{py: 1.5, '&:last-child': {pb: 1.5}}}>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{fontWeight: 700, letterSpacing: '0.08em', display: 'block', mb: 0.75}}
            >
              {t`DELAY REASON(S)`}
            </Typography>
            <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap'}}>
              {selectedDelayReasons.map((r) => (
                <Box key={r.code} sx={{display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.6, borderRadius: '20px', bgcolor: 'rgba(230,81,0,0.13)'}}>
                  <Box sx={{width: 6, height: 6, borderRadius: '50%', bgcolor: '#bf360c', flexShrink: 0, alignSelf: 'center'}} />
                  <Box component='span' sx={{fontSize: '0.8rem', fontWeight: 600, color: '#7f2400', letterSpacing: '0.01em', lineHeight: 1.4}}>
                    {r.code} – {r.labelEN}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {submitError && <Alert severity='error' onClose={() => setSubmitError(null)}>{submitError}</Alert>}
      {submitSuccess && <Alert severity='success' onClose={() => setSubmitSuccess(null)}>{submitSuccess}</Alert>}

      <Divider />

      {/* Footer */}
      <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
        <Button variant='text' startIcon={<ArrowBack />} sx={{color: 'text.secondary'}} onClick={() => navigate(-1)}>
          {t`Back`}
        </Button>
        <Box sx={{display: 'flex', gap: 1}}>
          <Button
            variant='outlined'
            color='inherit'
            startIcon={<RestartAlt />}
            sx={{color: 'text.secondary', borderColor: 'divider', '&:hover': {borderColor: 'text.secondary', bgcolor: 'action.hover'}}}
            onClick={() => navigate('/declarations/flight')}
          >
            {t`New Declaration`}
          </Button>
          <Button
            variant='contained'
            color='primary'
            startIcon={isLoading && pendingMethod === 'download' ? <CircularProgress size={16} color='inherit' /> : <Download />}
            disabled={isLoading}
            onClick={() => handlePreview('download')}
            sx={{fontWeight: 600}}
          >
            {t`Download PDF`}
          </Button>
          <Button
            variant='contained'
            color='warning'
            startIcon={isLoading && pendingMethod === 'email' ? <CircularProgress size={16} color='inherit' /> : <Email />}
            sx={{fontWeight: 600}}
            disabled={isLoading}
            onClick={() => handlePreview('email')}
          >
            {isLoading && pendingMethod === 'email' ? t`Processing...` : t`Send Declaration`}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DelayReviewPage;
